import { useState } from 'react';
import {
  DIAS_VENCIMENTO_SALDO,
  DIAS_VENCIMENTO_SINAL,
  DOCUMENTOS,
  MAX_PARCELAS_SALDO,
  cobrancaJaPaga,
  formatarBRL,
  valoresVigentes,
  type Candidato,
  type Cobranca,
  type MarcoId,
  type ModalidadeCobranca,
  type TipoCobranca,
  type TipoDocumento,
} from '@aponte/shared';
import type { DocumentoNaFicha } from '../../api/cliente.js';
import { useSessao } from '../../autenticacao/ContextoDeSessao.js';

interface Props {
  candidato: Candidato;
  cobrancas: Cobranca[];
  documentos: DocumentoNaFicha[];
  ocupado: boolean;
  aoEmitir: (tipo: TipoDocumento) => void;
  aoRegistrarAssinatura: (documento: DocumentoNaFicha) => void;
  aoCobrar: (tipo: TipoCobranca, modalidade: ModalidadeCobranca) => void;
  aoDecidirCuradoria: (aprovado: boolean) => void;
  aoMarcarAceite: () => void;
  aoRegistrarOnboarding: () => void;
}

/**
 * A esteira de A Ponte, na ordem em que a operação executa.
 *
 * Cada passo só libera quando o anterior tem seu marco carimbado — a mesma regra que a
 * API aplica do lado do servidor. Aqui a trava é conforto do operador; lá é garantia.
 *
 * Emissão e assinatura são passos **separados** de propósito: gerar o PDF não prova que
 * o Proponente concordou com nada, e é o documento assinado que move o contrato adiante.
 */
export function Esteira({
  candidato,
  cobrancas,
  documentos,
  ocupado,
  aoEmitir,
  aoRegistrarAssinatura,
  aoCobrar,
  aoDecidirCuradoria,
  aoMarcarAceite,
  aoRegistrarOnboarding,
}: Props) {
  const { temPapel } = useSessao();
  const { sinal, saldo } = valoresVigentes(candidato);
  const marcos = candidato.marcos;
  /*
   * O que o operador libera no link do saldo. Não é a quantidade de parcelas: essa quem
   * escolhe é o pagador, na tela do checkout. Aqui se decide apenas se o parcelamento
   * está disponível para ele.
   */
  const [modalidadeDoSaldo, setModalidadeDoSaldo] = useState<ModalidadeCobranca>('avista');

  const cobrancaDe = (tipo: TipoCobranca) =>
    cobrancas.filter((c) => c.tipo === tipo).at(-1) ?? null;

  /** Versão vigente de cada tipo — a assinatura se registra sobre a mais nova. */
  const documentoDe = (tipo: TipoDocumento): DocumentoNaFicha | null =>
    documentos
      .filter((d) => d.tipo === tipo)
      .sort((a, b) => a.versao - b.versao)
      .at(-1) ?? null;

  const podeCurar = temPapel('curadoria');
  const podeOperar = temPapel('closer');

  const passos: Passo[] = [
    {
      nome: 'Carta de Intenções',
      descricao:
        `Emitida com os dados da ficha e o sinal de ${formatarBRL(sinal)} já calculado. ` +
        'A emissão congela os valores do contrato: mudança futura de tabela não afeta mais ' +
        'este candidato.',
      liberado: true,
      marco: 'cartaEmitida',
      acoes: podeOperar ? (
        <>
          <BotaoDeEmissao
            documento={documentoDe('carta')}
            rotulo="Carta"
            ocupado={ocupado}
            aoEmitir={() => aoEmitir('carta')}
          />
          <AvisoDeDivergencia documento={documentoDe('carta')} />
        </>
      ) : null,
    },
    {
      nome: 'Carta assinada de volta',
      descricao:
        'Válida até a decisão do comitê, desde que o sinal seja pago em até ' +
        `${DIAS_VENCIMENTO_SINAL} dias corridos da assinatura (cl. 7.1).`,
      liberado: !!marcos.cartaEmitida,
      marco: 'cartaAssinadaRecebida',
      acoes: podeOperar ? (
        <BotaoDeAssinatura
          documento={documentoDe('carta')}
          ocupado={ocupado}
          aoRegistrar={aoRegistrarAssinatura}
        />
      ) : null,
    },
    {
      nome: `Link do sinal · 5% · ${formatarBRL(sinal)}`,
      descricao:
        'À vista, cartão ou Pix, sem parcelamento. Split de 70% para a Academia aplicado ' +
        'pelo gateway. O pagamento confirmado pelo Asaas move a ficha para curadoria sozinho.',
      liberado: !!marcos.cartaEmitida,
      marco: 'sinalPago',
      acoes: podeOperar ? (
        <BotaoDeCobranca
          cobranca={cobrancaDe('sinal')}
          rotulo="Gerar link do sinal"
          ocupado={ocupado}
          // Sinal é sempre à vista (Carta, cl. 3.2) — não há modalidade a escolher.
          aoCobrar={() => aoCobrar('sinal', 'avista')}
        />
      ) : null,
    },
    {
      nome: 'Curadoria',
      descricao:
        'Decisão de Gilson Rodrigues em até 10 dias úteis contados do sinal compensado. ' +
        'Sem aprovação, o sinal é devolvido integralmente em até 5 dias úteis.',
      liberado: !!marcos.sinalPago,
      marco: 'curadoriaDecidida',
      acoes: podeCurar ? (
        <>
          <button
            className="bt pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(true)}
          >
            Aprovar
          </button>
          <button
            className="bt perigo pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(false)}
          >
            Não aprovar
          </button>
        </>
      ) : (
        <span className="aguardando">Decisão exclusiva do comitê de curadoria.</span>
      ),
    },
    {
      nome: 'Termo de Aceite e Regimento',
      descricao:
        'Emitidos somente após a aprovação. O Regimento Interno é anexo indissociável do Termo.',
      liberado: ['aprovado', 'aceite', 'ativo'].includes(candidato.etapa),
      marco: 'termoEmitido',
      acoes: podeOperar ? (
        <>
          <BotaoDeEmissao
            documento={documentoDe('termo')}
            rotulo="Termo"
            ocupado={ocupado}
            aoEmitir={() => aoEmitir('termo')}
          />
          <BotaoDeEmissao
            documento={documentoDe('regimento')}
            rotulo="Regimento"
            ocupado={ocupado}
            /* O Regimento é anexo do Termo e assina junto: travá-lo pela assinatura do
               Termo evita anexo novo pendurado em contrato já firmado. */
            travadoPor={documentoDe('termo')}
            aoEmitir={() => aoEmitir('regimento')}
          />
          <AvisoDeDivergencia documento={documentoDe('termo')} />
        </>
      ) : null,
    },
    {
      nome: 'Termo assinado e aceite',
      descricao:
        'É o Termo assinado que firma o contrato — emitir o PDF não comprova aceitação. ' +
        'Só depois disso a cadeira pode ser aceita e o saldo, cobrado.',
      liberado: !!marcos.termoEmitido,
      marco: 'termoAssinadoRecebido',
      acoes: podeOperar ? (
        <>
          <BotaoDeAssinatura
            documento={documentoDe('termo')}
            ocupado={ocupado}
            aoRegistrar={aoRegistrarAssinatura}
          />
          {marcos.termoAssinadoRecebido && candidato.etapa === 'aprovado' && (
            <button className="bt pequeno" disabled={ocupado} onClick={aoMarcarAceite}>
              Marcar aceite
            </button>
          )}
        </>
      ) : null,
    },
    {
      nome: `Link do saldo · 95% · ${formatarBRL(saldo)}`,
      descricao: `À vista no cartão, Pix ou boleto, ou parcelado no cartão em até ${MAX_PARCELAS_SALDO}x — quem escolhe quantas parcelas é o pagador. Mesmo split de 70/30. Vencimento em ${DIAS_VENCIMENTO_SALDO} dias.`,
      liberado: !!marcos.termoAssinadoRecebido,
      marco: 'saldoPago',
      acoes: podeOperar ? (
        <>
          {!cobrancaDe('saldo') && (
            <>
              <label htmlFor="modalidade" style={{ margin: 0 }}>
                Modalidade
              </label>
              <select
                id="modalidade"
                value={modalidadeDoSaldo}
                onChange={(e) => setModalidadeDoSaldo(e.target.value as ModalidadeCobranca)}
                style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
              >
                <option value="avista">À vista · cartão, Pix ou boleto</option>
                <option value="parcelado">Parcelado · cartão, até {MAX_PARCELAS_SALDO}x</option>
              </select>
            </>
          )}
          <BotaoDeCobranca
            cobranca={cobrancaDe('saldo')}
            rotulo="Gerar link do saldo"
            ocupado={ocupado}
            aoCobrar={() => aoCobrar('saldo', modalidadeDoSaldo)}
          />
        </>
      ) : null,
    },
    {
      nome: 'Onboarding',
      descricao:
        'Entrega do selo numerado e do cartão em cerimônia com o comitê e presença do fundador.',
      liberado: !!marcos.saldoPago,
      marco: 'onboardingRealizado',
      acoes: podeOperar ? (
        <button className="bt vazado pequeno" disabled={ocupado} onClick={aoRegistrarOnboarding}>
          Registrar onboarding
        </button>
      ) : null,
    },
  ];

  return (
    <div>
      {passos.map((passo, i) => (
        <div key={passo.nome} className={`passo ${passo.liberado ? '' : 'bloqueado'}`}>
          <div className="numero">{String(i + 1).padStart(2, '0')}</div>
          <div className="corpo">
            <div className="nome">{passo.nome}</div>
            <div className="descricao">{passo.descricao}</div>
            <div className="acoes">
              {passo.liberado ? (
                <>
                  {passo.acoes}
                  {marcos[passo.marco] && (
                    <span className="feito">
                      ✓ {new Date(marcos[passo.marco]!).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </>
              ) : (
                <span className="aguardando">Aguarda a etapa anterior</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface Passo {
  nome: string;
  descricao: string;
  liberado: boolean;
  marco: MarcoId;
  acoes: React.ReactNode;
}

/**
 * Botão de emissão que conhece o estado do documento.
 *
 * Reemitir é legítimo enquanto ninguém assinou — corrigir um dado errado exige PDF novo, e
 * é para isso que existe o versionamento. Depois da assinatura, deixa de ser: o servidor
 * recusa, e a tela para de oferecer.
 */
function BotaoDeEmissao({
  documento,
  rotulo,
  ocupado,
  travadoPor,
  aoEmitir,
}: {
  documento: DocumentoNaFicha | null;
  rotulo: string;
  ocupado: boolean;
  travadoPor?: DocumentoNaFicha | null;
  aoEmitir: () => void;
}) {
  const assinado = documento?.assinadoRecebidoEm ?? travadoPor?.assinadoRecebidoEm;

  if (assinado) {
    return (
      <span className="arquivo-assinado">
        {rotulo} v{documento?.versao ?? '—'} assinado — não pode ser reemitido.
      </span>
    );
  }

  return (
    <button className="bt pequeno" disabled={ocupado} onClick={aoEmitir}>
      {ocupado
        ? 'Aguarde…'
        : documento
          ? `Reemitir ${rotulo} (v${documento.versao + 1})`
          : `Emitir ${rotulo}`}
    </button>
  );
}

/** Aviso de que o PDF emitido já não bate com a ficha. */
function AvisoDeDivergencia({ documento }: { documento: DocumentoNaFicha | null }) {
  if (!documento?.desatualizado) return null;
  return (
    <span className="aviso-desatualizado">
      ⚠ A ficha mudou depois desta emissão (v{documento.versao}) — reemita antes de assinar.
    </span>
  );
}

function BotaoDeAssinatura({
  documento,
  ocupado,
  aoRegistrar,
}: {
  documento: DocumentoNaFicha | null;
  ocupado: boolean;
  aoRegistrar: (documento: DocumentoNaFicha) => void;
}) {
  if (!documento) {
    return <span className="aguardando">Emita o documento primeiro.</span>;
  }

  if (documento.assinadoRecebidoEm) {
    return (
      <span className="arquivo-assinado">
        {DOCUMENTOS[documento.tipo].nome} v{documento.versao} assinado
        {documento.chaveS3Assinado ? ', com arquivo anexado' : ', sem arquivo anexado'}.
      </span>
    );
  }

  return (
    <button className="bt pequeno" disabled={ocupado} onClick={() => aoRegistrar(documento)}>
      Registrar assinatura
    </button>
  );
}

/**
 * Botão de cobrança que reflete o estado real do gateway.
 *
 * Oferecer "Gerar link" para uma cobrança já paga é oferecer uma ação impossível: o
 * servidor recusa com 409, e o operador descobre isso depois de clicar e esperar. A trava
 * de verdade continua sendo do servidor — esta aqui existe para a tela não mentir.
 */
function BotaoDeCobranca({
  cobranca,
  rotulo,
  ocupado,
  aoCobrar,
}: {
  cobranca: Cobranca | null;
  rotulo: string;
  ocupado: boolean;
  aoCobrar: () => void;
}) {
  if (cobranca && cobrancaJaPaga(cobranca)) {
    return (
      <>
        <span className="selo positivo">
          Pago · {formatarBRL(cobranca.valorRecebido ?? cobranca.valor)}
          {cobranca.valorRecebido != null && cobranca.valorRecebido !== cobranca.valor
            ? ' líquido'
            : ''}
        </span>
        <LinkDaCobranca cobranca={cobranca} />
      </>
    );
  }

  if (cobranca?.situacao === 'PENDENTE') {
    return (
      <>
        <span className="selo atencao">Link ativo · aguardando pagamento</span>
        <LinkDaCobranca cobranca={cobranca} />
        <button className="bt vazado pequeno" disabled={ocupado} onClick={aoCobrar}>
          Reenviar o mesmo link
        </button>
      </>
    );
  }

  return (
    <>
      <button className="bt pequeno" disabled={ocupado} onClick={aoCobrar}>
        {cobranca ? `${rotulo} (a anterior ${cobranca.situacao.toLowerCase()})` : rotulo}
      </button>
      <LinkDaCobranca cobranca={cobranca} />
    </>
  );
}

function LinkDaCobranca({ cobranca }: { cobranca: Cobranca | null }) {
  const [copiado, setCopiado] = useState(false);
  if (!cobranca) return null;

  return (
    <>
      <a
        href={cobranca.urlFatura}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 11 }}
      >
        Abrir fatura ({cobranca.situacao.toLowerCase()})
      </a>
      <button
        className="copiar"
        style={{ marginTop: 0 }}
        onClick={() => {
          void navigator.clipboard.writeText(cobranca.urlFatura).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          });
        }}
      >
        {copiado ? 'Copiado' : 'Copiar link'}
      </button>
    </>
  );
}
